import { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useGetStatistics } from "@/api/useStatistics"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { getDate } from "@/helpers/date.helpers"
import { TIME_FILTERS } from "@/helpers/filters.helper"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { useRuntimeCoverage } from "@/hooks/useRuntimeCoverage"
import { useFeatureFlags } from "@/hooks/useUser"

import type { Severity } from "@/domain/vulnerability"
import type { Dayjs } from "@/helpers/date.helpers.types"
import type { Config } from "@/hooks/useHttp.types"
import type { Filters } from "./useStatistics.types"

export const getDefaultSinceDate = () => getDate("2025-10-01")

export const getDefaultFilter = (defaultFilter?: Partial<Filters>) => ({
  since: getDefaultSinceDate(),
  ...defaultFilter
})

export const getInsightDefaultFilters = () => ({
  runtime: true,
  fixable: true,
  exploitMaturity: false
})

export const getFilters = (filters: Partial<Filters> = {}) => {
  const severities = [
    "critical",
    "high",
    "medium",
    "low"
  ] as const satisfies Severity[]
  const discovered_time_from = (
    filters.since || getDefaultFilter().since
  ).unix()

  return {
    discovered_time_from,
    runtime: filters.runtime ? "YES" : undefined,
    fixable: filters.fixable ? "YES" : undefined,
    exploitMaturity: filters.exploitMaturity ? "POC,IN_THE_WILD" : undefined,
    severity:
      severities
        .filter(
          (severity): severity is (typeof severities)[number] =>
            filters[severity] === true
        )
        .map((severity) => capitalizeFirstLetter(severity))
        .join(",") || undefined
  }
}

export const useStatisticsDashboard = <T, S = T>(
  props: { needle: string; defaultFilter?: Partial<Filters> },
  config?: Config<T, S>
) => {
  const { needle, defaultFilter = {} } = props
  const kortexInstalledFF = useFeatureFlags("kortexInstalled")
  const state = useState<Partial<Filters>>(getDefaultFilter(defaultFilter))
  const { runtime: _runtime, ...params } = getFilters(state[0])
  const { isLowRuntimeCoverage } = useRuntimeCoverage()
  const isEnabled =
    kortexInstalledFF !== undefined && isLowRuntimeCoverage !== undefined
  const runtime = kortexInstalledFF ? _runtime : undefined

  const { isLoading, ...context } = useGetStatistics<T, S>(
    { needle, runtime, ...params },
    { enabled: kortexInstalledFF !== undefined, ...config }
  )

  const navigation = useNavigate()

  const getDefaultFilterRange = (since: Dayjs) =>
    `${since.local().unix()},${getDate().local().endOf("day").unix()}`

  const redirectToTriage = useCallback(
    (extra?: Record<string, string>) => {
      const discoveredTime = TIME_FILTERS.DISCOVERED

      const additional = { ...extra }

      if (!extra?.[discoveredTime])
        additional[discoveredTime] = getDefaultFilterRange(
          getDefaultFilter().since
        )

      const currentParams = { runtime, ...params, ...additional }

      const queryString = new URLSearchParams()

      Object.entries(currentParams).forEach(([key, value]) => {
        if (value === undefined || value === null) return

        if (key === "discovered_time_from") return

        queryString.set(key, `${value}`)
      })

      const { TRIAGE } = NAMES_ROUTES
      const { ROOT, ISSUES } = TRIAGE

      navigation(`/${ROOT}/${ISSUES}?${queryString.toString()}`)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state[0], isEnabled]
  )

  return [
    { isLoading: isLoading || !isEnabled, ...context },
    state,
    { redirectToTriage, getDefaultFilterRange }
  ] as const
}
